import React, { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Fab, Slide, Tab, Tabs, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import Currency from 'react-currency-formatter';
import { useExteriorSignage } from 'services/Data/ExteriorSignageServices';
import useImage from 'services/Hooks/useImage';
import {
  useActiveExteriorSignageQuote,
  usePostBrpClubExteriorSignageQuoteItem,
  usePostExteriorSignageQuoteItem,
} from 'services/Data/ExteriorSignageQuoteService';
import { IsClub } from 'config/config';
import { useSelector } from 'react-redux';
import { Add, AttachFile, Remove } from '@mui/icons-material';
import ImagesSlideView from 'Components/ImagesSlideView';
import { useParams } from 'react-router';
import { useExteriorSignageCategories } from 'services/Data/ExteriorSignageCategoryService';
import { isLimitedExteriorSignageCategory } from 'services/Utilities/categoryHelpers';
import { TabContext, TabPanel } from '@mui/lab';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';

const useStyles = makeStyles(theme => ({
  mainButton: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 1rem rgba(255, 222, 0, 0.9)',
    },
  },
  guideLink: {
    color: 'rgba(51, 51, 51, 0.8)',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(27, 27, 27, 0.8)',
      textDecoration: 'underline',
    },
  },
  warningClass: {
    '& ul': {
      paddingLeft: '20px',
    },
  },
  tabsList: {
    position: 'relative',
    minHeight: 10,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :hover': {
      color: theme.palette.secondary.main,

      '& >svg': {
        color: theme.palette.secondary.main,
      },
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main}`,
    },
  },
  tabItem: {
    maxWidth: 'none',
    minWidth: 10,
    minHeight: 10,
    paddingLeft: 20,
    paddingRight: 20,
    textTransform: 'none',
    color: theme.palette.common.black,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightRegular,
  },
  fileLink: {
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

const ImageLanguageEnum = {
  Other: 0,
  English: 1,
  French: 2,
};

const normalizeImageLanguage = language => {
  if (typeof language === 'number') {
    return language;
  }

  if (typeof language === 'string') {
    const normalized = language.trim().toLowerCase();

    if (normalized === 'english') {
      return ImageLanguageEnum.English;
    }

    if (normalized === 'french') {
      return ImageLanguageEnum.French;
    }

    if (normalized === 'other') {
      return ImageLanguageEnum.Other;
    }

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const ExteriorSignageQuote = () => {
  const classes = useStyles();
  const { dealerPortalMenuProductId, exteriorSignageId } = useParams();

  const { t } = useTranslation();
  const isFrench = t('locale') === 'fr';
  const accountReducer = useSelector(state => state.AccountReducer);
  const initialSignageId = dealerPortalMenuProductId || exteriorSignageId;

  const { nameReference } = useLanguageReference();

  const { data: signage, isLoading: gettingExteriorSignage } = useExteriorSignage(initialSignageId);
  const { data: exteriorSignageCategories } = useExteriorSignageCategories();
  const { data: exteriorSignageQuote } = useActiveExteriorSignageQuote(accountReducer.isLoggedIn);
  const { getExteriorSignageImage } = useImage();
  const {
    mutateAsync: addExteriorSignageQuoteItem,
    isPending: addingExteriorSignageQuoteItem,
  } = usePostExteriorSignageQuoteItem(accountReducer.isDemo);
  const {
    mutateAsync: addBrpClubExteriorSignageQuoteItem,
    isPending: addingBrpClubExteriorSignageQuoteItem,
  } = usePostBrpClubExteriorSignageQuoteItem(accountReducer.isDemo);

  const [quantity, setQuantity] = useState(1);
  const [, setSelectedImgId] = useState(null);
  const [currentTabValue, setCurrentTabValue] = useState(1);

  const selectedSignageCategory = exteriorSignageCategories?.find(
    category => category.id === signage?.exteriorSignageCategory_Id,
  );
  const showQuantitySelector = isLimitedExteriorSignageCategory(selectedSignageCategory);
  const price = signage?.oneBrandPrice || 0;
  const signageTitle = isFrench
    ? signage?.nameFr || signage?.nameEn || signage?.name
    : signage?.nameEn || signage?.nameFr || signage?.name;
  const signageDescription = isFrench
    ? signage?.descriptionFr || signage?.descriptionEn || signage?.description
    : signage?.descriptionEn || signage?.descriptionFr || signage?.description;
  const signageWarning = isFrench
    ? signage?.warningFr || signage?.warningEn || signage?.warning
    : signage?.warningEn || signage?.warningFr || signage?.warning;

  const localizedSignageImages = useMemo(() => {
    const allImages = Array.isArray(signage?.images) ? signage.images : [];

    if (!allImages.length) {
      return [];
    }

    const requestedLanguage = isFrench ? ImageLanguageEnum.French : ImageLanguageEnum.English;
    const localizedImages = allImages.filter(image => {
      const imageLanguage = normalizeImageLanguage(image?.language);
      return imageLanguage === requestedLanguage || imageLanguage === ImageLanguageEnum.Other;
    });

    if (localizedImages.length > 0) {
      return localizedImages;
    }

    const languageAgnosticImages = allImages.filter(image => normalizeImageLanguage(image?.language) === null);

    if (languageAgnosticImages.length > 0) {
      return languageAgnosticImages;
    }

    return allImages;
  }, [isFrench, signage?.images]);

  const validInstallationFiles = signage?.userGuideFiles?.filter(ugf => ugf[nameReference]) || [];

  const formatAreaValue = useCallback(
    value => {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null;
      }

      return Number(value).toLocaleString(isFrench ? 'fr-CA' : 'en-CA', {
        maximumFractionDigits: 2,
      });
    },
    [isFrench],
  );

  const totalSquareFeet = formatAreaValue(signage?.totalSquareFeet);
  const totalSquareCentimeters = formatAreaValue(signage?.totalSquareCentimeters);
  const hasAreaValues = !!totalSquareFeet || !!totalSquareCentimeters;

  const handleAddSignage = _ => {
    const exteriorSignageQuoteItem = {
      id: uuidv4(),
      exteriorSignage_Id: signage?.id,
      unitPrice: price,
      currencyName: signage?.currencyName,
      sku: signage?.sku,
      quantity: quantity || 1,
      exteriorSignageQuote_Id: exteriorSignageQuote?.id,
    };

    const addExteriorSignageMethod = IsClub ? addBrpClubExteriorSignageQuoteItem : addExteriorSignageQuoteItem;

    addExteriorSignageMethod(exteriorSignageQuoteItem).then(_ => {
      setQuantity(1);
    });
  };

  const handleDownloadFile = useCallback((fileUrl, fileName) => {
    axios
      .get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Content-Disposition': 'attachment',
        },
      })
      .then(response => {
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(response.headers['content-disposition']);
        if (matches != null && matches[1]) {
          const originalFileName = matches[1].replace(/['"]/g, '');
          const extension = originalFileName.split('.')[1];

          link.setAttribute('download', `${fileName}.${extension}`);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }, []);

  const selectedQuantity = quantity || 1;

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull} sx={{ m: 2 }}>
        <GridContainer justifyContent="center" alignItems="flex-start" sx={{ height: '100%' }}>
          <GridItem xs={12} xl={8} lg={7} md={6}>
            <ImagesSlideView
              isLoading={gettingExteriorSignage}
              images={localizedSignageImages}
              getImageUrl={imgId => getExteriorSignageImage(signage?.id, imgId)}
              onUpdateImage={img => setSelectedImgId(img?.id)}
              mainImageContainerSx={{ ml: { xs: 1, md: 4 }, mr: { xs: 1, md: 4 } }}
              thumbnailContainerSx={{ ml: { xs: 1, md: 4 }, mr: { xs: 1, md: 4 } }}
              mainImageStyle={{ maxHeight: '78vh', width: '100%' }}
              enableMagnifier
            />
          </GridItem>
          <GridItem xs={12} xl={4} lg={5} md={6}>
            <CmtCard sx={{ height: '100%' }} className={classes.itemCard}>
              <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%' }}>
                <Box display="flex" flexDirection={'column'} style={{ width: '100%', marginBottom: '1vh' }}>
                  <Typography variant="h1" sx={{ fontSize: { xs: 20, md: 26 } }}>
                    {signageTitle}
                  </Typography>
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                    {signage?.sku}
                  </Typography>
                  <Divider />
                </Box>

                <Box display="flex" flexDirection={'column'}>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginTop: '1vh',
                      flexDirection: 'column',
                    }}>
                    {signage && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Typography fontSize={25} fontWeight="bold">
                          <Currency quantity={price * selectedQuantity} currency={exteriorSignageQuote?.currencyName} />
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {signage && <Divider sx={{ my: 2 }} />}

                  {signage && <Typography fontSize={25}></Typography>}

                  <Box
                    sx={{
                      justifyContent: signage && showQuantitySelector ? 'space-between' : 'flex-end',
                      alignItems: 'center',
                      display: 'flex',
                      pt: 0,
                    }}>
                    {signage && showQuantitySelector && (
                      <Box
                        fontSize={20}
                        style={{ width: '15vh' }}
                        sx={{ mt: 4, mb: 5 }}
                        display="flex"
                        justifyContent={'space-between'}
                        alignItems={'center'}>
                        <Fab
                          size="small"
                          color="secondary"
                          className={classes.squareFab}
                          sx={{
                            color: '#000',
                          }}
                          disabled={selectedQuantity <= 1}
                          onClick={() => setQuantity(current => Math.max(1, (current || 1) - 1))}>
                          <Remove />
                        </Fab>
                        <Typography>{selectedQuantity}</Typography>
                        <Fab
                          size="small"
                          color="secondary"
                          className={classes.squareFab}
                          sx={{
                            color: '#000',
                          }}
                          disabled={selectedQuantity >= 3}
                          onClick={() => setQuantity(current => (current || 1) + 1)}>
                          <Add />
                        </Fab>
                      </Box>
                    )}
                    <Button
                      key={uuidv4()}
                      disabled={
                        (IsClub ? addingBrpClubExteriorSignageQuoteItem : addingExteriorSignageQuoteItem) || !signage?.id
                      }
                      variant={'contained'}
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                        fontWeight: 800,
                        fontSize: 14,
                        mt: 2,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#EFBA29',
                          boxShadow: 'none',
                        },
                      }}
                      onClick={handleAddSignage}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 22,
                          fontWeight: 800,
                          paddingRight: 0,
                          paddingLeft: 0,
                          paddingTop: 2,
                          paddingBottom: 2,
                          fontFamily: 'Trade Gothic Next LT Pro',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}>
                        {t('addToCart')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </CmtCardContent>
            </CmtCard>

            <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
              <CmtCardContent>
                {signage ? (
                  <Box>
                    <TabContext value={currentTabValue}>
                      <Tabs
                        className={classes.tabsList}
                        value={currentTabValue}
                        onChange={(_, value) => {
                          setCurrentTabValue(value);
                        }}
                        aria-label="profile tabs"
                        variant="scrollable"
                        allowScrollButtonsMobile>
                        <Tab className={classes.tabItem} value={1} label={t('overview')} />
                        <Tab className={classes.tabItem} value={2} label={t('brpRequirements')} />
                      </Tabs>

                      <TabPanel value={1}>
                        <Typography variant="h2" sx={{ mt: 2 }}>{`${
                          signage.variableWidth
                            ? t('variantSignageWidth')
                            : signage.width
                            ? t('signageWidth', { width: signage.width })
                            : ''
                        }${signage.height ? `, ${t('signageHeight', { height: signage.height })}` : ''}`}</Typography>
                        {hasAreaValues && (
                          <Typography variant="h5" sx={{ mt: 2 }}>
                            {t('signageArea', {
                              totalSquareFeet: totalSquareFeet || '-',
                              totalSquareCentimeters: totalSquareCentimeters || '-',
                            })}
                          </Typography>
                        )}
                        <Typography sx={{ mt: 5 }} component="div">
                          <div dangerouslySetInnerHTML={{ __html: signageDescription }} />
                        </Typography>

                        {signageWarning && (
                          <Typography sx={{ mt: 2 }} variant="body2" component="div" className={classes.warningClass}>
                            <div dangerouslySetInnerHTML={{ __html: signageWarning }} />
                          </Typography>
                        )}
                      </TabPanel>

                      <TabPanel value={2}>
                        <Typography>{validInstallationFiles.length > 0 ? t('filesInfo') : t('noFiles')}</Typography>
                        <SkeletonWrapper
                          skeletonProps={{
                            height: 36,
                          }}
                          fullWidth
                          isLoading={gettingExteriorSignage}
                          component={
                            signage && (
                              <Box sx={{ ml: 5 }}>
                                <ul>
                                  {validInstallationFiles.map(ugf => (
                                    <li key={ugf.id}>
                                      <span
                                        onClick={() =>
                                          handleDownloadFile(getExteriorSignageImage(signage.id, ugf.id), ugf[nameReference])
                                        }
                                        className={classes.fileLink}>
                                        <AttachFile color="secondary" fontSize="small" sx={{ marginBottom: '-0.3rem' }} />{' '}
                                        {ugf[nameReference]}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </Box>
                            )
                          }
                        />
                      </TabPanel>
                    </TabContext>
                  </Box>
                ) : (
                  t('no_signage_category_selected')
                )}
              </CmtCardContent>
            </CmtCard>
          </GridItem>
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default ExteriorSignageQuote;
